import React from "react";

/**
 * MuscleMap — clickable anatomical body chart for filtering the exercise
 * library by muscle group.  Two side-by-side silhouettes (front + back) with
 * each muscle group rendered as a semi-transparent overlay path.
 *
 * Tapping a region calls `onSelect(muscleGroup)` and drives the parent's
 * `muscle` filter state; the currently-selected group is filled in blaze red.
 *
 * Muscle groups match `MUSCLE_GROUPS` on the backend so a single click on
 * `shoulders` returns exercises tagged front/side/rear delts too.
 */

const GROUPS = [
  { id: "chest",      label: "Chest" },
  { id: "shoulders",  label: "Shoulders" },
  { id: "biceps",     label: "Biceps" },
  { id: "triceps",    label: "Triceps" },
  { id: "back",       label: "Back / Lats" },
  { id: "core",       label: "Core" },
  { id: "quads",      label: "Quads" },
  { id: "glutes",     label: "Glutes" },
  { id: "hamstrings", label: "Hamstrings" },
  { id: "calves",     label: "Calves" },
];

// Front-view muscle overlay paths. Coordinates fit a 200x420 viewBox.
// Simplified anatomical shapes — not medical-grade but instantly readable.
const FRONT = {
  chest: "M 70 110 Q 100 100 130 110 L 130 145 Q 100 152 70 145 Z",
  shoulders_l: "M 55 100 Q 70 92 82 102 L 76 122 Q 62 118 55 108 Z",
  shoulders_r: "M 145 100 Q 130 92 118 102 L 124 122 Q 138 118 145 108 Z",
  biceps_l: "M 52 130 Q 60 128 66 132 L 62 168 Q 54 168 50 160 Z",
  biceps_r: "M 148 130 Q 140 128 134 132 L 138 168 Q 146 168 150 160 Z",
  core: "M 78 150 L 122 150 L 120 210 L 80 210 Z",
  quads_l: "M 78 230 Q 88 230 92 235 L 90 300 Q 84 302 78 302 Z",
  quads_r: "M 122 230 Q 112 230 108 235 L 110 300 Q 116 302 122 302 Z",
  calves_l: "M 80 340 Q 88 340 90 345 L 88 390 Q 82 392 78 390 Z",
  calves_r: "M 120 340 Q 112 340 110 345 L 112 390 Q 118 392 122 390 Z",
};

// Back-view overlay paths (mirror the same silhouette layout).
const BACK = {
  back: "M 72 108 Q 100 100 128 108 L 130 180 Q 100 186 70 180 Z",
  shoulders_l: "M 55 100 Q 70 92 82 102 L 76 120 Q 62 118 55 108 Z",
  shoulders_r: "M 145 100 Q 130 92 118 102 L 124 120 Q 138 118 145 108 Z",
  triceps_l: "M 52 130 Q 60 128 66 132 L 62 168 Q 54 168 50 160 Z",
  triceps_r: "M 148 130 Q 140 128 134 132 L 138 168 Q 146 168 150 160 Z",
  glutes: "M 74 210 Q 100 205 126 210 L 128 245 Q 100 252 72 245 Z",
  hamstrings_l: "M 78 250 Q 88 250 92 255 L 90 315 Q 84 316 78 314 Z",
  hamstrings_r: "M 122 250 Q 112 250 108 255 L 110 315 Q 116 316 122 314 Z",
  calves_l: "M 80 340 Q 88 340 90 345 L 88 390 Q 82 392 78 390 Z",
  calves_r: "M 120 340 Q 112 340 110 345 L 112 390 Q 118 392 122 390 Z",
};

// Rough head + body outline for context (non-clickable).
const OUTLINE = (
  <>
    <circle cx="100" cy="60" r="26" />
    {/* Torso */}
    <path d="M 68 92 Q 100 84 132 92 L 138 220 Q 100 226 62 220 Z" />
    {/* Arms */}
    <path d="M 62 100 L 48 200 L 56 200 L 74 120 Z" />
    <path d="M 138 100 L 152 200 L 144 200 L 126 120 Z" />
    {/* Legs */}
    <path d="M 70 220 L 68 400 L 92 400 L 96 220 Z" />
    <path d="M 130 220 L 132 400 L 108 400 L 104 220 Z" />
  </>
);

function Region({ d, id, active, onClick, label }) {
  const isOn = active === id;
  return (
    <path
      d={d}
      onClick={() => onClick(id)}
      role="button"
      tabIndex={0}
      aria-label={label}
      className={`cursor-pointer transition-colors ${
        isOn
          ? "fill-blaze/90 stroke-blaze"
          : "fill-blaze/25 stroke-blaze/40 hover:fill-blaze/50 hover:stroke-blaze/70"
      }`}
      strokeWidth="1"
      data-testid={`muscle-region-${id}`}
    />
  );
}

export default function MuscleMap({ active, onSelect, counts = {} }) {
  const pick = (id) => onSelect(active === id ? "" : id);

  return (
    <div className="obs-card" data-testid="muscle-map">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline">Filter by</div>
          <h3 className="font-heading text-2xl uppercase">Muscle map</h3>
        </div>
        {active ? (
          <button
            type="button"
            onClick={() => onSelect("")}
            className="ghost-btn px-3 py-1 text-[11px] uppercase tracking-widest"
            data-testid="muscle-map-clear"
          >
            Clear · {active}
          </button>
        ) : (
          <span className="text-[10px] text-zinc-500 uppercase tracking-widest">
            Tap a muscle
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-6 justify-center">
        {/* FRONT view */}
        <figure className="flex flex-col items-center">
          <svg viewBox="0 0 200 420" className="w-40 h-auto" aria-label="Front view">
            <g stroke="currentColor" strokeWidth="1" fill="rgba(255,255,255,0.04)" className="text-obsidian-600">
              {OUTLINE}
            </g>
            <Region id="chest" label="Chest" d={FRONT.chest} active={active} onClick={pick} />
            <Region id="shoulders" label="Shoulders" d={FRONT.shoulders_l} active={active} onClick={pick} />
            <Region id="shoulders" label="Shoulders" d={FRONT.shoulders_r} active={active} onClick={pick} />
            <Region id="biceps" label="Biceps" d={FRONT.biceps_l} active={active} onClick={pick} />
            <Region id="biceps" label="Biceps" d={FRONT.biceps_r} active={active} onClick={pick} />
            <Region id="core" label="Core" d={FRONT.core} active={active} onClick={pick} />
            <Region id="quads" label="Quads" d={FRONT.quads_l} active={active} onClick={pick} />
            <Region id="quads" label="Quads" d={FRONT.quads_r} active={active} onClick={pick} />
            <Region id="calves" label="Calves" d={FRONT.calves_l} active={active} onClick={pick} />
            <Region id="calves" label="Calves" d={FRONT.calves_r} active={active} onClick={pick} />
          </svg>
          <figcaption className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
            Front
          </figcaption>
        </figure>

        {/* BACK view */}
        <figure className="flex flex-col items-center">
          <svg viewBox="0 0 200 420" className="w-40 h-auto" aria-label="Back view">
            <g stroke="currentColor" strokeWidth="1" fill="rgba(255,255,255,0.04)" className="text-obsidian-600">
              {OUTLINE}
            </g>
            <Region id="back" label="Back / Lats" d={BACK.back} active={active} onClick={pick} />
            <Region id="shoulders" label="Shoulders" d={BACK.shoulders_l} active={active} onClick={pick} />
            <Region id="shoulders" label="Shoulders" d={BACK.shoulders_r} active={active} onClick={pick} />
            <Region id="triceps" label="Triceps" d={BACK.triceps_l} active={active} onClick={pick} />
            <Region id="triceps" label="Triceps" d={BACK.triceps_r} active={active} onClick={pick} />
            <Region id="glutes" label="Glutes" d={BACK.glutes} active={active} onClick={pick} />
            <Region id="hamstrings" label="Hamstrings" d={BACK.hamstrings_l} active={active} onClick={pick} />
            <Region id="hamstrings" label="Hamstrings" d={BACK.hamstrings_r} active={active} onClick={pick} />
            <Region id="calves" label="Calves" d={BACK.calves_l} active={active} onClick={pick} />
            <Region id="calves" label="Calves" d={BACK.calves_r} active={active} onClick={pick} />
          </svg>
          <figcaption className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
            Back
          </figcaption>
        </figure>

        {/* Chip legend — keyboard-friendly fallback for tiny touch targets */}
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] text-zinc-500 uppercase tracking-widest mb-2">
            Quick pick
          </div>
          <div className="flex flex-wrap gap-1.5">
            {GROUPS.map((g) => {
              const on = active === g.id;
              const n = counts[g.id];
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => pick(g.id)}
                  className={`text-[10px] uppercase tracking-widest px-3 py-1 border rounded-full transition-colors ${
                    on
                      ? "bg-blaze text-obsidian-900 border-blaze"
                      : "border-obsidian-600 text-zinc-400 hover:border-blaze hover:text-blaze"
                  }`}
                  data-testid={`muscle-chip-${g.id}`}
                >
                  {g.label}
                  {typeof n === "number" ? ` · ${n}` : ""}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
